import React from 'react';
import { Button } from '../components/ui/Button';
import { Target, Zap, ArrowRight, Database, CheckCircle2 } from 'lucide-react';
export function Services() {
  return <div className="w-full pt-20" data-id="element-884">
      {/* Hero Section */}
      <section className="py-24 md:py-40 bg-engage-black text-center px-4 md:pt-[240px] md:pb-[240px]" data-id="element-885">
        <div className="max-w-4xl mx-auto" data-id="element-886">
          <h1 className="text-3xl md:text-7xl font-black text-engage-white mb-6 tracking-tight" data-id="element-887">
            The Engage Machine
          </h1>
          <p className="text-lg md:text-3xl font-bold text-engage-yellow tracking-wide" data-id="element-888">
            A Growth System Built for Scale
          </p>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-24 md:py-32 bg-engage-gray1 border-y border-engage-gray2" data-id="element-889">
        <div className="max-w-6xl mx-auto px-4" data-id="element-890">
          <div className="text-center" data-id="element-891">
            <h2 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-892">
              What Is The Engage Machine?
            </h2>
            <p className="text-lg md:text-xl text-engage-gray4 leading-relaxed max-w-3xl mx-auto" data-id="element-893">
              The Engage Machine is not a service — it's a system. We combine
              media buying, creative production, funnel strategy, and growth
              architecture into one unified engine designed to turn attention
              into revenue.
            </p>
          </div>
        </div>
      </section>

      {/* Services Sections */}
      <div className="flex flex-col" data-id="element-894">
        {/* 1. Conversion */}
        <section className="py-24 bg-engage-black" data-id="element-895">
          <div className="max-w-7xl mx-auto px-4 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-896">
            <div className="order-1 flex flex-col" data-id="element-897">
              <div className="w-20 h-20 bg-engage-gray1 rounded-2xl flex items-center justify-center mb-8 border border-engage-gray2" data-id="element-898">
                <Target className="w-10 h-10 text-engage-yellow" data-id="element-899" />
              </div>
              <span className="text-engage-yellow font-bold tracking-wider uppercase text-sm mb-4 block" data-id="element-900">
                If You Don't Convert, Nothing Else Matters
              </span>
              <h2 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-901">
                Conversion
              </h2>
              <p className="text-engage-gray4 text-lg mb-8 leading-relaxed" data-id="element-902">
                Traffic is useless if it doesn't turn into revenue. We architect
                high-converting landing pages, funnels, and user journeys
                designed to maximize the value of every single click.
              </p>
              <ul className="space-y-4 mb-10" data-id="element-903">
                {['Higher conversion rates', 'Lower cost per acquisition', 'More revenue from same traffic', 'Foundation ready to scale'].map((item, i) => <li key={i} className="flex items-center text-engage-gray5" data-id="element-904">
                    <CheckCircle2 className="w-5 h-5 text-engage-yellow mr-3 shrink-0" data-id="element-905" />
                    <span data-id="element-906">{item}</span>
                  </li>)}
              </ul>
              <Button to="/contact" variant="primary" className="self-start" data-id="element-907">
                Optimize Your Funnel
              </Button>
            </div>
            <div className="order-2 bg-engage-gray1 rounded-lg aspect-square lg:aspect-auto lg:h-[600px] border border-engage-gray2 relative overflow-hidden" data-id="element-908">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(252,198,13,0.1)_0%,transparent_70%)]" data-id="element-909" />
              {/* Abstract representation of conversion funnel */}
              <div className="absolute inset-0 flex flex-col items-center justify-center space-y-4 p-12" data-id="element-910">
                <div className="w-full h-16 bg-engage-gray2 rounded-md border border-engage-gray3 opacity-50" data-id="element-911" />
                <div className="w-3/4 h-16 bg-engage-gray2 rounded-md border border-engage-gray3 opacity-70" data-id="element-912" />
                <div className="w-1/2 h-16 bg-engage-gray2 rounded-md border border-engage-gray3 opacity-90" data-id="element-913" />
                <div className="w-1/4 h-16 bg-engage-yellow rounded-md shadow-[0_0_30px_rgba(252,198,13,0.3)]" data-id="element-914" />
              </div>
            </div>
          </div>
        </section>

        {/* 2. Content */}
        <section className="py-24 bg-engage-gray1" data-id="element-915">
          <div className="max-w-7xl mx-auto px-4 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-916">
            <div className="order-2 lg:order-1 bg-engage-black rounded-lg aspect-square lg:aspect-auto lg:h-[600px] border border-engage-gray2 relative overflow-hidden" data-id="element-917">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(252,198,13,0.1)_0%,transparent_70%)]" data-id="element-918" />
              {/* Abstract representation of content grid */}
              <div className="absolute inset-0 grid grid-cols-2 gap-4 p-12" data-id="element-919">
                <div className="bg-engage-gray2 rounded-md border border-engage-gray3" data-id="element-920" />
                <div className="bg-engage-gray2 rounded-md border border-engage-gray3" data-id="element-921" />
                <div className="bg-engage-yellow/20 rounded-md border border-engage-yellow/50 shadow-[0_0_30px_rgba(252,198,13,0.1)]" data-id="element-922" />
                <div className="bg-engage-gray2 rounded-md border border-engage-gray3" data-id="element-923" />
              </div>
            </div>
            <div className="order-1 lg:order-2" data-id="element-924">
              <div className="w-20 h-20 bg-engage-black rounded-2xl flex items-center justify-center mb-8 border border-engage-gray2" data-id="element-925">
                <Zap className="w-10 h-10 text-engage-yellow" data-id="element-926" />
              </div>
              <span className="text-engage-yellow font-bold tracking-wider uppercase text-sm mb-4 block" data-id="element-927">
                This Is What Actually Gets Attention
              </span>
              <h2 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-928">Content</h2>
              <p className="text-engage-gray4 text-lg mb-8 leading-relaxed" data-id="element-929">
                In a world of infinite scrolling, average creative gets ignored.
                We produce scroll-stopping, performance-driven video and static
                content engineered specifically for paid social platforms.
              </p>
              <ul className="space-y-4 mb-10" data-id="element-930">
                {['Higher engagement and CTR', 'Stronger brand positioning', 'Consistent high-performing creatives', 'Reduced creative fatigue'].map((item, i) => <li key={i} className="flex items-center text-engage-gray5" data-id="element-931">
                    <CheckCircle2 className="w-5 h-5 text-engage-yellow mr-3 shrink-0" data-id="element-932" />
                    <span data-id="element-933">{item}</span>
                  </li>)}
              </ul>
              <Button to="/contact" variant="primary" data-id="element-934">
                Build Your Creative System
              </Button>
            </div>
          </div>
        </section>

        {/* 3. Traffic */}
        <section className="py-24 bg-engage-black" data-id="element-935">
          <div className="max-w-7xl mx-auto px-4 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-936">
            <div className="order-1" data-id="element-937">
              <div className="w-20 h-20 bg-engage-gray1 rounded-2xl flex items-center justify-center mb-8 border border-engage-gray2" data-id="element-938">
                <ArrowRight className="w-10 h-10 text-engage-yellow" data-id="element-939" />
              </div>
              <span className="text-engage-yellow font-bold tracking-wider uppercase text-sm mb-4 block" data-id="element-940">
                Now We Bring People In
              </span>
              <h2 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-941">Traffic</h2>
              <p className="text-engage-gray4 text-lg mb-8 leading-relaxed" data-id="element-942">
                We deploy sophisticated media buying strategies across Meta,
                Google, TikTok, and more. Our approach isn't just about spending
                money—it's about buying data efficiently to find your most
                profitable customers.
              </p>
              <ul className="space-y-4 mb-10" data-id="element-943">
                {['Consistent scalable traffic', 'Higher quality leads', 'Predictable customer acquisition', 'Growth without single channel dependency'].map((item, i) => <li key={i} className="flex items-center text-engage-gray5" data-id="element-944">
                    <CheckCircle2 className="w-5 h-5 text-engage-yellow mr-3 shrink-0" data-id="element-945" />
                    <span data-id="element-946">{item}</span>
                  </li>)}
              </ul>
              <Button to="/contact" variant="primary" data-id="element-947">
                Scale Your Traffic
              </Button>
            </div>
            <div className="order-2 bg-engage-gray1 rounded-lg aspect-square lg:aspect-auto lg:h-[600px] border border-engage-gray2 relative overflow-hidden" data-id="element-948">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(252,198,13,0.1)_0%,transparent_70%)]" data-id="element-949" />
              {/* Abstract representation of traffic routing */}
              <div className="absolute inset-0 flex items-center justify-center p-12" data-id="element-950">
                <div className="relative w-full h-full flex items-center justify-between" data-id="element-951">
                  <div className="flex flex-col space-y-8 w-1/4" data-id="element-952">
                    <div className="h-4 bg-engage-gray3 rounded-full w-full" data-id="element-953" />
                    <div className="h-4 bg-engage-gray3 rounded-full w-3/4" data-id="element-954" />
                    <div className="h-4 bg-engage-gray3 rounded-full w-full" data-id="element-955" />
                  </div>
                  <div className="w-1/2 h-1 bg-engage-yellow/30 relative" data-id="element-956">
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 rounded-full border-4 border-engage-yellow shadow-[0_0_30px_rgba(252,198,13,0.3)]" data-id="element-957" />
                  </div>
                  <div className="w-1/4 h-32 bg-engage-gray2 rounded-lg border border-engage-gray3" data-id="element-958" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 4. Data */}
        <section className="py-24 bg-engage-gray1" data-id="element-959">
          <div className="max-w-7xl mx-auto px-4 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-960">
            <div className="order-2 lg:order-1 bg-engage-black rounded-lg aspect-square lg:aspect-auto lg:h-[600px] border border-engage-gray2 relative overflow-hidden" data-id="element-961">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(252,198,13,0.1)_0%,transparent_70%)]" data-id="element-962" />
              {/* Abstract representation of data charts */}
              <div className="absolute inset-0 flex items-end justify-center p-12 space-x-4" data-id="element-963">
                <div className="w-12 h-1/4 bg-engage-gray3 rounded-t-sm" data-id="element-964" />
                <div className="w-12 h-2/4 bg-engage-gray3 rounded-t-sm" data-id="element-965" />
                <div className="w-12 h-1/3 bg-engage-gray3 rounded-t-sm" data-id="element-966" />
                <div className="w-12 h-3/4 bg-engage-yellow rounded-t-sm shadow-[0_0_30px_rgba(252,198,13,0.3)]" data-id="element-967" />
                <div className="w-12 h-full bg-engage-yellow rounded-t-sm shadow-[0_0_30px_rgba(252,198,13,0.3)]" data-id="element-968" />
              </div>
            </div>
            <div className="order-1 lg:order-2" data-id="element-969">
              <div className="w-20 h-20 bg-engage-black rounded-2xl flex items-center justify-center mb-8 border border-engage-gray2" data-id="element-970">
                <Database className="w-10 h-10 text-engage-yellow" data-id="element-971" />
              </div>
              <span className="text-engage-yellow font-bold tracking-wider uppercase text-sm mb-4 block" data-id="element-972">
                This Is Where Scaling Actually Happens
              </span>
              <h2 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-973">Data</h2>
              <p className="text-engage-gray4 text-lg mb-8 leading-relaxed" data-id="element-974">
                Gut feelings don't scale. We build robust tracking
                infrastructure and custom dashboards so every decision is backed
                by hard numbers, allowing us to identify winning patterns and
                scale them aggressively.
              </p>
              <ul className="space-y-4 mb-10" data-id="element-975">
                {['Smarter decisions backed by data', 'Increased ROAS', 'Continuous performance improvement', 'System that scales efficiently'].map((item, i) => <li key={i} className="flex items-center text-engage-gray5" data-id="element-976">
                    <CheckCircle2 className="w-5 h-5 text-engage-yellow mr-3 shrink-0" data-id="element-977" />
                    <span data-id="element-978">{item}</span>
                  </li>)}
              </ul>
              <Button to="/contact" variant="primary" data-id="element-979">
                Scale With Data
              </Button>
            </div>
          </div>
        </section>
      </div>

      {/* Final CTA */}
      <section className="py-32 bg-engage-black text-center px-4 relative overflow-hidden" data-id="element-980">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(252,198,13,0.05)_0%,transparent_70%)]" data-id="element-981" />
        <div className="max-w-3xl mx-auto relative z-10" data-id="element-982">
          <h2 className="text-4xl md:text-6xl font-bold mb-6 tracking-tight" data-id="element-983">
            Activate The Engage Machine
          </h2>
          <p className="text-xl text-engage-gray4 mb-10" data-id="element-984">
            Let's build a performance system designed to scale your brand
            profitably.
          </p>
          <Button to="/contact" variant="primary" className="text-lg px-10 py-4" data-id="element-985">
            Book a Call With Us
          </Button>
        </div>
      </section>
    </div>;
}